import { createReducer, requestStatus } from '../../../../lib/Utils'
import {
  SEARCH_CAMPAIGNS_BY_RETAILER,
  SEARCH_CAMPAIGNS_BY_RETAILER_FULFILLED,
  SEARCH_CAMPAIGNS_BY_RETAILER_REJECTED,
} from './actions'

/**
 * @typedef RetailersCampaignsSearchStore
 * @property {object} byRetailerId
 * @property {CampaignsSearchRetailer} byRetailerId[{number}]
 */

/**
 * @typedef CampaignsSearchRetailer
 * @property {object[][]} searchCampaigns
 * @property {object} searchCampaignsFilters
 * @property {{status: requestStatus, error: object|array|null, offset: number}[]} searchCampaignsRequests
 * @property {number} [searchCampaignsTotal] - total number of results for the current search
 */

/**
 * @type RetailersCampaignsSearchStore
 */
const initialState = {
  byRetailerId: {},
}

export const checkIfCampaignFiltersChanged = (previousFilters, newFilters) => {
  let filtersChanged = false
  let prevFilterKeys = Object.keys(previousFilters).filter(
    (value) => value !== 'offset',
  )
  let newFilterKeys = Object.keys(newFilters).filter(
    (value) => value !== 'offset',
  )
  if (prevFilterKeys.length !== newFilterKeys.length) {
    filtersChanged = true
  } else {
    prevFilterKeys.forEach((key) => {
      if (typeof newFilters[key] === 'undefined') {
        filtersChanged = true
      } else {
        if (key === 'filters' && newFilters[key]) {
          filtersChanged =
            JSON.stringify(previousFilters[key]) !==
            JSON.stringify(newFilters[key])
        } else if (key === 'fields' || key === 'tags') {
          if (previousFilters[key].length !== newFilters[key].length) {
            filtersChanged = true
          } else {
            let unmatchedKey = previousFilters[key].findIndex(
              (currentKey) => newFilters[key].indexOf(currentKey) === -1,
            )
            filtersChanged = unmatchedKey !== -1
          }
        } else if (previousFilters[key] !== newFilters[key]) {
          filtersChanged = true
        }
      }
      return !filtersChanged
    })
  }
  return filtersChanged
}

export default createReducer(initialState, {
  [SEARCH_CAMPAIGNS_BY_RETAILER]: (state, action) => {
    if (!action.payload.retailerId) {
      return state
    }
    const prevRetailer =
      (state.byRetailerId && state.byRetailerId[action.payload.retailerId]) ||
      {}
    let {
      searchCampaigns = [],
      searchCampaignsRequests = [],
      searchCampaignsFilters,
      searchCampaignsTotal,
    } = prevRetailer
    const { offset = 0, ...filters } = action.payload
    // Check if the filters match the previous filters
    if (searchCampaignsFilters) {
      const filtersChanged = checkIfCampaignFiltersChanged(
        searchCampaignsFilters,
        filters,
      )
      if (filtersChanged) {
        // Clear data and requests
        searchCampaignsRequests = []
        searchCampaigns = []
        searchCampaignsFilters = filters
        searchCampaignsTotal = null
      }
    } else {
      searchCampaignsFilters = filters
    }
    let page = action.payload.perPage ? offset / action.payload.perPage : 0
    searchCampaignsRequests = searchCampaignsRequests.slice(0)
    searchCampaignsRequests[page] = {
      status: requestStatus.pending,
      offset,
      error: null,
    }
    const retailer = {
      ...prevRetailer,
      searchCampaigns,
      searchCampaignsFilters,
      searchCampaignsRequests,
      searchCampaignsTotal,
    }
    return {
      ...state,
      byRetailerId: {
        ...state.byRetailerId,
        [action.payload.retailerId]: retailer,
      },
    }
  },
  [SEARCH_CAMPAIGNS_BY_RETAILER_FULFILLED]: (state, action) => {
    const { retailerId, offset = 0 } = action.payload.filters
    const previousRetailer =
      (state.byRetailerId && state.byRetailerId[retailerId]) || {}
    const page = action.payload.filters.perPage
      ? offset / action.payload.filters.perPage
      : 0
    let {
      searchCampaigns = [],
      searchCampaignsRequests = [],
    } = previousRetailer
    // Save page data
    searchCampaigns = searchCampaigns.slice(0)
    searchCampaigns[page] = action.payload.response
    // Update request status
    searchCampaignsRequests = searchCampaignsRequests.slice(0)
    searchCampaignsRequests[page] = {
      status: requestStatus.complete,
      error: null,
      offset,
      lastUpdate: action.payload.requestDate,
    }
    // Update total
    const searchCampaignsTotal = action.payload.total
    const retailer = {
      ...previousRetailer,
      searchCampaignsRequests,
      searchCampaignsTotal,
      searchCampaigns,
    }
    return {
      ...state,
      byRetailerId: {
        ...state.byRetailerId,
        [retailerId]: retailer,
      },
    }
  },
  [SEARCH_CAMPAIGNS_BY_RETAILER_REJECTED]: (state, action) => {
    if (action.payload instanceof Error) {
      return state
    }
    const { retailerId, offset = 0 } = action.payload.filters
    const previousRetailer =
      state.byRetailerId && state.byRetailerId[retailerId]
    let { searchCampaignsRequests = [] } = previousRetailer
    const page = action.payload.filters.perPage
      ? offset / action.payload.filters.perPage
      : 0
    // Update request status
    searchCampaignsRequests = searchCampaignsRequests.slice(0)
    searchCampaignsRequests[page] = {
      status: requestStatus.rejected,
      error: action.payload.response,
      offset,
      lastUpdate: action.payload.requestDate,
    }
    const retailer = {
      ...previousRetailer,
      searchCampaignsRequests,
    }
    return {
      ...state,
      byRetailerId: {
        ...state.byRetailerId,
        [retailerId]: retailer,
      },
    }
  },
})
